import Button from '@/common/components/Button'
import { ImageUploader } from '@/common/components/ImageUploader'
import MaskedTextField from '@/common/components/MaskedTextField'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextField from '@/common/components/TextField'
import { cnpjCpfMask, numericRegexMask, stateInscriptionMask } from '@/common/constants/masks.constant'
import { useVaduCnpjDetails } from '@/common/hooks/queries/useVaduCnpjDetails'
import { VaduCnpjDetailsResponse } from '@/common/types/vaduCnpjDetails.type'
import { unFormatCnpj } from '@/common/utils/unFormatters'
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview'
import { ReviewButtons } from '@/features/establishmentLeadsFunnel/components/ReviewButtons'
import { PublicCompanyLeadsFunnelUpdateValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { GeneralInformationData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { UseMutateFunction } from '@tanstack/react-query'
import { Dispatch, SetStateAction, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

interface GeneralInformationSectionProps {
  general_information?: GeneralInformationData
  submittableOnBlur?: (
    e: React.FocusEvent<HTMLInputElement, Element>,
    onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => void
  submit?: () => void
  file?: Blob | undefined
  setFile?: Dispatch<SetStateAction<Blob | undefined>>
  approveGeneralInformationLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  declineGeneralInformationLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  disabled?: boolean
}

export const GeneralInformationSection = ({
  general_information,
  submittableOnBlur,
  submit,
  file,
  setFile,
  approveGeneralInformationLead,
  declineGeneralInformationLead,
  disabled,
}: GeneralInformationSectionProps) => {
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    control,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<PublicCompanyLeadsFunnelUpdateValidationSchema>()

  const successGetVaduCnpjDetails = (data: VaduCnpjDetailsResponse) => {
    const { general_information } = data.data.company

    const oldGeneralInformation = getValues('general_information')

    setValue('general_information', {
      ...oldGeneralInformation,
      cnpj: general_information.cnpj || '',
      fantasy_name: general_information.fantasy_name || '',
      official_name: general_information.official_name || '',
      email: general_information.email || '',
      primary_cnae: unFormatCnpj(general_information.primary_cnae || ''),
    })

    submit?.()
  }

  const { mutate: getVaduCnpjDetails, isPending: vaduCnpjDetailsIsPending } = useVaduCnpjDetails({
    onSuccess: successGetVaduCnpjDetails,
    onError: (error) => {
      setMessages((error.response?.data as { errors: { base: string[] } })?.errors?.base || null)
    },
  })

  const handleImportCnpjData = () => {
    getVaduCnpjDetails(unFormatCnpj(general_information?.cnpj || ''))
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between">
        <p className="text-[18px] font-bold">Dados Gerais</p>
        {approveGeneralInformationLead && declineGeneralInformationLead && (
          <ReviewButtons
            reviewState={general_information?.last_review?.state}
            onApprove={approveGeneralInformationLead}
            onDecline={declineGeneralInformationLead}
          />
        )}
      </div>
      {general_information?.last_review?.state === 'disapproved' && (
        <RequestedReview description={general_information?.last_review?.description} />
      )}
      <div className="flex gap-2">
        <div className="flex gap-2 w-full">
          <ImageUploader
            isLoading={false}
            label="Foto"
            name="profile-picture"
            accept="image/*"
            handleChange={(file) => {
              setFile?.(file)
              submit?.()
            }}
            currentImageUrl={
              file
                ? URL.createObjectURL(file)
                : general_information?.company_logo?.url
                ? general_information?.company_logo?.url
                : undefined
            }
            disabled={disabled}
          />
          <div className="flex flex-col w-full items-start gap-y-1">
            <MaskedTextField
              mask={cnpjCpfMask}
              className="w-full mt-[14px]"
              label="CNPJ/CPF"
              defaultValue={general_information?.cnpj}
              errorMessage={errors?.general_information?.cnpj?.message}
              {...register('general_information.cnpj')}
              disabled
              markAsRequired
            />
            <Button
              name="import_cnpj_data"
              variant="link"
              onClick={handleImportCnpjData}
              disabled={
                disabled ||
                !(general_information?.cnpj && general_information?.cnpj?.length >= 14) ||
                vaduCnpjDetailsIsPending
              }
              isLoading={vaduCnpjDetailsIsPending}
            >
              Importar dados
            </Button>
          </div>
        </div>
        <TextField label="Tipo de empresa" className="w-full mt-[14px]" disabled value="Pública" markAsRequired />
      </div>
      <div className="flex gap-2">
        <Controller
          control={control}
          name="general_information.fantasy_name"
          defaultValue={general_information?.fantasy_name}
          disabled={disabled}
          render={({ field: { onBlur, ...rest } }) => (
            <TextField
              label="Nome Fantasia"
              markAsRequired
              className="w-1/2"
              maxLength={64}
              {...rest}
              errorMessage={errors?.general_information?.fantasy_name?.message}
              onBlur={(e) => submittableOnBlur?.(e, onBlur)}
            />
          )}
        />
        <Controller
          control={control}
          name="general_information.official_name"
          defaultValue={general_information?.official_name}
          disabled={disabled}
          render={({ field: { onBlur, ...rest } }) => (
            <TextField
              label="Nome Empresarial"
              markAsRequired
              className="w-1/2"
              maxLength={64}
              {...rest}
              errorMessage={errors?.general_information?.official_name?.message}
              onBlur={(e) => submittableOnBlur?.(e, onBlur)}
            />
          )}
        />
      </div>
      <Controller
        control={control}
        name="general_information.email"
        defaultValue={general_information?.email}
        disabled={disabled}
        render={({ field: { onBlur, ...rest } }) => (
          <TextField
            label="Endereço eletrônico"
            markAsRequired
            maxLength={64}
            {...rest}
            errorMessage={errors?.general_information?.email?.message}
            onBlur={(e) => submittableOnBlur?.(e, onBlur)}
          />
        )}
      />
      <div className="flex gap-2">
        <Controller
          control={control}
          name="general_information.state_inscription"
          defaultValue={general_information?.state_inscription}
          disabled={disabled}
          render={({ field: { onBlur, ...rest } }) => (
            <MaskedTextField
              label="IE (Inscrição Estadual)"
              markAsRequired
              mask={stateInscriptionMask}
              className="w-1/2"
              {...rest}
              errorMessage={errors?.general_information?.state_inscription?.message}
              onBlur={(e) => submittableOnBlur?.(e, onBlur)}
            />
          )}
        />
        <Controller
          control={control}
          name="general_information.primary_cnae"
          defaultValue={general_information?.primary_cnae}
          disabled={disabled}
          render={({ field: { onBlur, ...rest } }) => (
            <MaskedTextField
              label="CNAE"
              mask={numericRegexMask(7)}
              className="w-1/2"
              {...rest}
              errorMessage={errors?.general_information?.primary_cnae?.message}
              onBlur={(e) => submittableOnBlur?.(e, onBlur)}
              markAsRequired
            />
          )}
        />
      </div>
      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </div>
  )
}
